// Shield Everywhere (cc_24): on-device link verdicts for Cipher Lock text using the server's ruleset
// (GET /shield/rules — refreshed daily), the pre-open link gate, and the save-time guard helper.
import { api } from "@/lib/api";

const RULES_KEY = "bloq_shield_rules";
const RULES_TTL = 24 * 3600 * 1000;
let rulesPromise = null;

export async function getShieldRules() {
  try {
    const cached = JSON.parse(localStorage.getItem(RULES_KEY) || "null");
    if (cached?.rules && Date.now() - cached.at < RULES_TTL) return cached.rules;
  } catch { /* ignore */ }
  if (!rulesPromise) {
    rulesPromise = api.shieldRules()
      .then((rules) => { localStorage.setItem(RULES_KEY, JSON.stringify({ at: Date.now(), rules })); return rules; })
      .catch(() => { try { return JSON.parse(localStorage.getItem(RULES_KEY) || "null")?.rules || null; } catch { return null; } })
      .finally(() => { rulesPromise = null; });
  }
  return rulesPromise;
}

// Server verdicts are safe|warning|blocked|unknown (docs say verified|caution). Normalize to one set.
export function normVerdict(v) {
  if (v === "verified" || v === "safe") return "safe";
  if (v === "caution" || v === "warning" || v === "danger") return "warning";
  if (v === "blocked") return "blocked";
  return "unknown";
}

const URL_RE = /(?:https?:\/\/)?(?:[a-z0-9\u00a1-\uffff-]+\.)+[a-z\u00a1-\uffff]{2,}(?::\d+)?(?:\/[^\s<>"']*)?/gi;
const LOOKALIKE = { 0: "o", 1: "l", 3: "e", 4: "a", 5: "s", 7: "t", "@": "a", "$": "s" };

function hostOf(raw) {
  const noScheme = raw.replace(/^https?:\/\//i, "");
  return noScheme.split(/[/?#:]/)[0].toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
}

function verdictFor(host, rules) {
  const labels = host.split(".");
  if (labels.some((l) => l.startsWith("xn--"))) return ["blocked", "Blocked · punycode lookalike domain"];
  const trusted = rules.trusted_domains || [];
  if (trusted.some((d) => host === d || host.endsWith(`.${d}`))) return ["safe", "Verified · Known domain"];
  const sld = labels.slice(0, -1).join(".") || host;
  const normalized = sld.replace(/[013457@$]/g, (c) => LOOKALIKE[c] || c);
  for (const brand of rules.brand_targets || []) {
    const official = rules.brand_official?.[brand];
    if (official && (host === official || host.endsWith(`.${official}`))) return ["safe", "Verified · Known domain"];
    if (normalized.includes(brand)) {
      if (!sld.includes(brand)) return ["blocked", `Blocked · imitates “${brand}” (lookalike characters)`];
      return ["blocked", official ? `Blocked · fake ${brand} domain (official: ${official})` : `Blocked · fake ${brand} domain`];
    }
  }
  if ((rules.shorteners || []).includes(host)) return ["warning", "Caution · shortened link hides destination"];
  if ((rules.risky_tlds || []).some((t) => host.endsWith(t))) return ["warning", "Caution · risky domain extension"];
  return ["unknown", "Unverified · open with care"];
}

/** Scan free text on-device → { links:[{url, verdict, label}], scam_intent }. Mirrors the server scanner. */
export function scanTextWithRules(text, rules) {
  if (!text || !rules) return { links: [], scam_intent: false };
  const links = [];
  const seen = new Set();
  for (const m of text.match(URL_RE) || []) {
    const hasScheme = /^https?:\/\//i.test(m);
    const host = hostOf(m);
    const tld = host.split(".").pop();
    const known = (rules.shorteners || []).includes(host) || (rules.trusted_domains || []).some((d) => host === d || host.endsWith(`.${d}`));
    if (!hasScheme && !known && !(rules.common_tlds || []).includes(tld)) continue;
    if (!host.includes(".") || seen.has(host)) continue;
    seen.add(host);
    const [verdict, label] = verdictFor(host, rules);
    links.push({ url: m.replace(/^https?:\/\//i, "").replace(/[.,;:!?)]+$/, ""), verdict, label });
  }
  let scam = false;
  try { scam = !!rules.scam_intent_re && new RegExp(rules.scam_intent_re, "i").test(text); } catch { scam = false; }
  return { links, scam_intent: scam };
}

export async function scanText(text) {
  return scanTextWithRules(text, await getShieldRules());
}

export const hrefFor = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/** Pre-open gate: safe links open directly, everything else goes through the interstitial (LinkGate). */
export function openGatedLink(link) {
  if (!link?.url) return;
  if (normVerdict(link.verdict) === "safe") {
    window.open(hrefFor(link.url), "_blank", "noopener,noreferrer");
    return;
  }
  window.dispatchEvent(new CustomEvent("bloq:linkgate", { detail: link }));
}

/** Save-time guard: returns detail.message for a 400 shield_blocked_link, else null. */
export function shieldBlockedMessage(err) {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 400 && d && typeof d === "object" && d.code === "shield_blocked_link") {
    return d.message || `Shield blocked a link in your text${d.url ? ` (${d.url})` : ""}. Remove it to save.`;
  }
  return null;
}
